import React, { useEffect, useRef, useState } from "react";
import Modal from "@mui/material/Modal";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { BiImageAdd } from "react-icons/bi";
import toast from "react-hot-toast";
import { resolveProductIntakeApi } from "../../../utils/productIntakeApi";

const modalBoxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 570,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  background: "#fff",
  borderRadius: "12px",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const removeImageButtonSx = {
  position: "absolute",
  top: 12,
  right: 12,
  width: 36,
  height: 36,
  background: "rgba(255, 255, 255, 0.96)",
  color: "#D32F2F",
  opacity: 0,
  transform: "translateY(-6px)",
  transition: "all 0.2s ease",
  zIndex: 1,
  "&:hover": {
    background: "#fff",
  },
};

const MAX_IMAGE_SIZE_BYTES = 1.5 * 1024 * 1024; // 1.5MB

const createEmptyImageSlot = () => ({
  file: null,
  preview: "",
});

export default function UploadPictureModal({
  open,
  handleClose,
  row,
  recordList,
  intakeScope = "admin",
}) {
  const intakeApi = resolveProductIntakeApi(intakeScope);
  const fileInputRefs = useRef([]);
  const imagesRef = useRef([createEmptyImageSlot()]);
  const [images, setImages] = useState([createEmptyImageSlot()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    if (open) {
      const existing = (row?.product_image || []).filter(Boolean);
      const existingSlots = existing.map((url) => ({ file: null, preview: url, isExisting: true }));
      setImages([...existingSlots, createEmptyImageSlot()]);
      setIsSubmitting(false);
    }
  }, [open, row?.id, row?.product_image]);

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  const cleanupAndClose = () => {
    images.forEach((image) => {
      if (image.preview && !image.isExisting) {
        URL.revokeObjectURL(image.preview);
      }
    });
    fileInputRefs.current = [];
    setImages([createEmptyImageSlot()]);
    setIsSubmitting(false);
    handleClose();
  };

  const handleFileChange = (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("Image must be under 1.5MB.");
      event.target.value = "";
      return;
    }

    const updatedImages = [...images];
    const previousPreview = updatedImages[index]?.preview;
    if (previousPreview) {
      URL.revokeObjectURL(previousPreview);
    }

    updatedImages[index] = {
      file,
      preview: URL.createObjectURL(file),
    };

    setImages(updatedImages);
    event.target.value = "";
  };

  const handleRemoveImage = async (event, index) => {
    event.stopPropagation();

    const selectedImage = images[index];
    if (!selectedImage) return;

    if (selectedImage.isExisting) {
      if (!row?.id || !selectedImage.preview) {
        toast.error("Image details are missing.");
        return;
      }

      try {
        setIsSubmitting(true);
        const response = await intakeApi.deleteImage({
          id: row.id,
          imageUrl: selectedImage.preview,
        });

        const filteredImages = images.filter((_, imageIndex) => imageIndex !== index);
        setImages(filteredImages.length ? filteredImages : [createEmptyImageSlot()]);
        recordList?.();
        toast.success(response?.data?.message || "Image deleted successfully");
      } catch (error) {
        toast.error(error?.data?.message || "Failed to delete image");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (selectedImage?.preview) {
      URL.revokeObjectURL(selectedImage.preview);
    }

    const filteredImages = images.filter((_, imageIndex) => imageIndex !== index);
    setImages(filteredImages.length ? filteredImages : [createEmptyImageSlot()]);

    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].value = "";
    }
  };

  const handleAddAnother = () =>
    setImages((previous) => [...previous, createEmptyImageSlot()]);

  const handleNext = async () => {
    const selectedFiles = images
      .map((image) => image.file)
      .filter(Boolean);

    if (!row?.id) {
      toast.error("Product id is missing.");
      return;
    }

    if (!selectedFiles.length) {
      toast.error("Please select at least one image.");
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_IMAGE_SIZE_BYTES
    );
    if (oversizedFile) {
      toast.error("Each image must be under 1.5MB.");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append("images", file);
    });

    try {
      setIsSubmitting(true);
      const response = await intakeApi.uploadImages({
        id: row.id,
        formData,
      });

      toast.success(response?.data?.message || "Images uploaded successfully");
      recordList?.();
      cleanupAndClose();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to upload images");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelectedImages = images.some((image) => image.file);
  const editableImageCount = images.filter((image) => !image.isExisting).length;
  const hasEmptyUploadSlot = images.some(
    (image) => !image.isExisting && !image.preview
  );

  return (
    <Modal open={open} onClose={isSubmitting ? undefined : cleanupAndClose}>
      <Box sx={modalBoxStyle}>
        <Box
          sx={{
            background: "#126ABC",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "16px" }}>
            Upload an Image of the product
          </Typography>
          <IconButton
            onClick={cleanupAndClose}
            sx={{ color: "#fff", p: 0 }}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <>
            <Box sx={{ p: "20px 24px", overflowY: "auto", flex: 1 }}>
              <Typography sx={{ fontWeight: 500, mb: 1, fontSize: "14px", color: "#333" }}>
                Product Image
              </Typography>

              {/* ── Existing images section ── */}
              {(row?.product_image || []).filter(Boolean).length > 0 && (
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#126ABC", mb: 1 }}>
                  Existing Images ({(row?.product_image || []).filter(Boolean).length})
                </Typography>
              )}

              {images.map((image, index) => (
                (() => {
                  const canRemoveSlot =
                    image.isExisting
                      ? Boolean(image.preview)
                      : Boolean(image.preview) || editableImageCount > 1;

                  return (
                    <Box
                      key={`${row?.id || "item"}-${index}`}
                      onClick={() => !image.isExisting && fileInputRefs.current[index]?.click()}
                      sx={{
                        border: image.isExisting ? "1px solid #b0c8e8" : "1px solid #ddd",
                        borderRadius: "8px",
                        height: "220px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: image.isExisting ? "default" : "pointer",
                        mb: 2,
                        overflow: "hidden",
                        background: image.isExisting ? "#eef4fb" : "#fafafa",
                        flexShrink: 0,
                        position: "relative",
                        "&:hover .remove-image-btn": {
                          opacity: canRemoveSlot ? 1 : 0,
                          transform: canRemoveSlot ? "translateY(0)" : "translateY(-6px)",
                        },
                        "&:hover .image-overlay": {
                          opacity: image.preview && !image.isExisting ? 1 : 0,
                        },
                      }}
                    >
                  {image.isExisting ? (
                    // ── Existing uploaded image (read-only) ──
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                      <IconButton
                        className="remove-image-btn"
                        onClick={(event) => handleRemoveImage(event, index)}
                        sx={removeImageButtonSx}
                        disabled={isSubmitting}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 6, left: 8,
                          background: "#126ABC",
                          color: "#fff",
                          fontSize: "10px",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: "20px",
                          zIndex: 1,
                        }}
                      >
                        Uploaded
                      </Box>
                      <img
                        src={image.preview}
                        alt={`existing-${index}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </Box>
                  ) : image.preview ? (
                    <>
                      <Box
                        className="image-overlay"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(12, 20, 33, 0.05) 25%, rgba(12, 20, 33, 0.6) 100%)",
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                        }}
                      />
                      <IconButton
                        className="remove-image-btn"
                        onClick={(event) => handleRemoveImage(event, index)}
                        sx={removeImageButtonSx}
                        disabled={isSubmitting}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <img
                        src={image.preview}
                        alt="product"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      {canRemoveSlot && (
                        <IconButton
                          className="remove-image-btn"
                          onClick={(event) => handleRemoveImage(event, index)}
                          sx={removeImageButtonSx}
                          disabled={isSubmitting}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: "50%",
                          background: "#FFF3E0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 1,
                        }}
                      >
                        <BiImageAdd size={26} color="#E17B0B" />
                      </Box>
                      <Typography
                        sx={{
                          color: "#E17B0B",
                          fontSize: "13px",
                          fontWeight: 500,
                        }}
                      >
                        Upload Product Image
                      </Typography>
                    </>
                  )}
                  {!image.isExisting && (
                    <input
                      type="file"
                      accept="image/*"
                      ref={(element) => { fileInputRefs.current[index] = element; }}
                      style={{ display: "none" }}
                      onChange={(event) => handleFileChange(event, index)}
                    />
                  )}
                    </Box>
                  );
                })()
              ))}
            </Box>

            <Box sx={{ p: "0 24px 20px", background: "#fff" }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddAnother}
                disabled={isSubmitting || hasEmptyUploadSlot}
                sx={{
                  borderColor: "#E17B0B",
                  color: "#E17B0B",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 500, 
                  mb: 2,
                  "&:hover": {
                    borderColor: "#E17B0B",
                    background: "#FFF3E0",
                  },
                  "&.Mui-disabled": {
                    borderColor: "#F0C9A0",
                    color: "#F0C9A0",
                  },
                }}
              >
                Add Another Image
              </Button>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isSubmitting || !hasSelectedImages}
                sx={{
                  background: "#126ABC",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "15px",
                  px: 5,
                  height: "46px",
                  "&:hover": { background: "#0f5a9e" },
                  "&.Mui-disabled": {
                    background: "#9BBEE4",
                    color: "#fff",
                  },
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    <span>Uploading...</span>
                  </Box>
                ) : (
                  "Submit"
                )}
              </Button>
              </Box>
            </Box>
        </>
      </Box>
    </Modal>
  );
}
